import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import bookPurchaseService from '../services/bookPurchaseService';

const Profile = () => {
    const { user, logout, refreshUser } = useAuth();
    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [profileData, setProfileData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        address: {
            street: user?.address?.street || '',
            city: user?.address?.city || '',
            state: user?.address?.state || '',
            pincode: user?.address?.pincode || ''
        }
    });
    const [subscription, setSubscription] = useState(null);
    const [testHistory, setTestHistory] = useState([]);
    const [practiceHistory, setPracticeHistory] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [editing, setEditing] = useState(false);
    const [profilePhoto, setProfilePhoto] = useState(user?.profilePhoto || '');
    const [photoUploading, setPhotoUploading] = useState(false);
    const [message, setMessage] = useState('');

    const fetchProfileData = async () => {
        setRefreshing(true);
        try {
            await Promise.all([
                fetchSubscription(),
                fetchTestHistory(),
                fetchPracticeHistory(),
                fetchPaymentHistory()
            ]);
        } catch (error) {
            console.error('Error fetching profile data:', error);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchProfileData();
    }, []);

    const fetchSubscription = async () => {
        try {
            const response = await api.get('/profile/subscription');
            setSubscription(response.data.subscription);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const fetchTestHistory = async () => {
        try {
            const response = await api.get('/profile/test-history');
            setTestHistory(response.data.mockTests || []);
        } catch (error) {
            console.error('Error fetching test history:', error);
        }
    };

    const fetchPracticeHistory = async () => {
        try {
            const response = await api.get('/profile/test-history');
            setPracticeHistory(response.data.practiceTests || []);
        } catch (error) {
            console.error('Error fetching practice history:', error);
        }
    };

    const fetchPaymentHistory = async () => {
        try {
            const purchases = await bookPurchaseService.getAllPurchases();
            setPaymentHistory(purchases);
        } catch (error) {
            console.error('Error fetching payment history:', error);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            const response = await api.put('/profile/update', profileData);
            if (response.data.success) {
                await refreshUser();
                setEditing(false);
                setMessage({ type: 'success', text: 'Profile updated successfully!' });
                await fetchProfileData();
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({ type: 'error', text: 'Failed to update profile' });
        } finally {
            setLoading(false);
        }
    };

    // ✅ FIXED: Convert image to base64 and send as JSON (backend expects photoUrl)
    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: 'Please select an image file' });
            return;
        }
        
        if (file.size > 2 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size should be less than 2MB' });
            return;
        }
        
        setPhotoUploading(true);
        
        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64String = reader.result;
            setProfilePhoto(base64String);
            
            try {
                const response = await api.post('/profile/upload-photo', { photoUrl: base64String });
                if (response.data.success) {
                    await refreshUser();
                    setProfilePhoto(response.data.photoUrl || base64String);
                    setMessage({ type: 'success', text: 'Profile photo updated!' });
                    await fetchProfileData();
                    setTimeout(() => setMessage(''), 3000);
                }
            } catch (error) {
                console.error('Error uploading photo:', error);
                setMessage({ type: 'error', text: 'Failed to upload photo' });
            } finally {
                setPhotoUploading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
    };

    const tabs = [
        { id: 'personal', label: 'Personal Info', icon: '👤' },
        { id: 'subscription', label: 'Subscription', icon: '💎' },
        { id: 'tests', label: 'Test History', icon: '📝' },
        { id: 'practice', label: 'Practice History', icon: '❓' },
        { id: 'payments', label: 'Payment History', icon: '💰' },
        { id: 'saved', label: 'Saved Content', icon: '⭐' },
        { id: 'settings', label: 'Settings', icon: '⚙️' }
    ];

    if (refreshing) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 mb-8 text-white">
                <div className="flex flex-col md:flex-row items-center gap-6">
                    {/* Profile Photo */}
                    <div className="relative">
                        <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold border-4 border-white shadow-lg overflow-hidden">
                            {profilePhoto ? (
                                <img src={profilePhoto} alt={user?.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                                user?.name?.charAt(0).toUpperCase()
                            )}
                        </div>
                        <label className="absolute bottom-0 right-0 bg-white rounded-full p-1 cursor-pointer shadow-md">
                            <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={photoUploading} />
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </label>
                        {photoUploading && (
                            <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-white"></div>
                            </div>
                        )}
                    </div>
                    <div className="text-center md:text-left">
                        <h1 className="text-2xl font-bold">{user?.name}</h1>
                        <p className="text-indigo-200">{user?.email}</p>
                        <p className="text-indigo-200 text-sm mt-1">Member since {formatDate(user?.createdAt)}</p>
                    </div>
                </div>
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            {/* Tabs Navigation */}
            <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-6 overflow-x-auto pb-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                            activeTab === tab.id
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <span>{tab.icon}</span>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
                {/* Personal Info Tab */}
                {activeTab === 'personal' && (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Personal Information</h2>
                            <button onClick={() => setEditing(!editing)} className="text-indigo-600 hover:text-indigo-700">
                                {editing ? 'Cancel' : 'Edit Profile'}
                            </button>
                        </div>
                        {editing ? (
                            <form onSubmit={handleProfileUpdate} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <input type="text" value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-indigo-500" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input type="email" value={profileData.email} disabled className="w-full px-4 py-2 border rounded-lg bg-gray-50" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input type="tel" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input type="text" placeholder="Street" value={profileData.address.street} onChange={(e) => setProfileData({...profileData, address: {...profileData.address, street: e.target.value}})} className="px-4 py-2 border rounded-lg" />
                                    <input type="text" placeholder="City" value={profileData.address.city} onChange={(e) => setProfileData({...profileData, address: {...profileData.address, city: e.target.value}})} className="px-4 py-2 border rounded-lg" />
                                    <input type="text" placeholder="State" value={profileData.address.state} onChange={(e) => setProfileData({...profileData, address: {...profileData.address, state: e.target.value}})} className="px-4 py-2 border rounded-lg" />
                                    <input type="text" placeholder="PIN Code" value={profileData.address.pincode} onChange={(e) => setProfileData({...profileData, address: {...profileData.address, pincode: e.target.value}})} className="px-4 py-2 border rounded-lg" />
                                </div>
                                <button type="submit" disabled={loading} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700">
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </form>
                        ) : (
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div><p className="text-gray-500 text-sm">Full Name</p><p className="font-medium">{user?.name}</p></div>
                                    <div><p className="text-gray-500 text-sm">Email Address</p><p className="font-medium">{user?.email}</p></div>
                                    <div><p className="text-gray-500 text-sm">Phone Number</p><p className="font-medium">{user?.phone || 'Not provided'}</p></div>
                                    <div><p className="text-gray-500 text-sm">Address</p><p className="font-medium">{user?.address?.street ? `${user.address.street}, ${user.address.city}, ${user.address.state} - ${user.address.pincode}` : 'Not provided'}</p></div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Subscription Tab */}
                {activeTab === 'subscription' && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Active Subscription</h2>
                        {subscription?.isActive ? (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <div className="flex justify-between items-center flex-wrap gap-4">
                                    <div>
                                        <p className="text-green-800 font-semibold">{subscription.plan}</p>
                                        <p className="text-sm text-green-700">Valid until: {formatDate(subscription.expiryDate)}</p>
                                        <p className="text-sm text-green-700">Days remaining: {subscription.daysLeft}</p>
                                    </div>
                                    <button className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm">Cancel Subscription</button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No active subscription</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Test History Tab */}
                {activeTab === 'tests' && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Mock Test History</h2>
                        {testHistory.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No tests attempted yet</p>
                        ) : (
                            <div className="space-y-3">
                                {testHistory.slice(0, 10).map((test, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium">{test.testTitle}</p>
                                            <p className="text-sm text-gray-500">{test.examName}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-indigo-600">{test.percentage?.toFixed(1)}%</p>
                                            <p className="text-sm text-gray-500">{formatDate(test.createdAt)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Practice History Tab */}
                {activeTab === 'practice' && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Practice History</h2>
                        {practiceHistory.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No practice sessions yet</p>
                        ) : (
                            <div className="space-y-3">
                                {practiceHistory.slice(0, 10).map((practice, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium">{practice.subject} - {practice.chapter}</p>
                                            <p className="text-sm text-gray-500">{practice.totalQuestions} questions</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-green-600">{practice.score}/{practice.totalMarks}</p>
                                            <p className="text-sm text-gray-500">{formatDate(practice.createdAt)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Payment History Tab */}
                {activeTab === 'payments' && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Payment History</h2>
                        {paymentHistory.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No payment history</p>
                        ) : (
                            <div className="space-y-3">
                                {paymentHistory.slice(0, 10).map((payment) => (
                                    <div key={payment.purchaseId} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium">{payment.title}</p>
                                            <p className="text-sm text-gray-500">
                                                {payment.itemType === 'book' ? 'Book' : payment.itemType === 'boardCategory' ? 'Board Practice Set' : 'Mock Test'}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-indigo-600">{formatCurrency(payment.amountPaid)}</p>
                                            <p className="text-sm text-gray-500">{formatDate(payment.purchasedAt)}</p>
                                            <span className={`text-xs px-2 py-0.5 rounded ${payment.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {payment.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Saved Content Tab */}
                {activeTab === 'saved' && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Saved Content</h2>
                        <div className="text-center py-8">
                            <p className="text-gray-500">⭐ Coming soon</p>
                            <p className="text-sm text-gray-400 mt-1">Saving books, practice sets, and mock tests for later will be available in a future update.</p>
                        </div>
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Account Settings</h2>
                        <div className="space-y-4">
                            <button onClick={logout} className="w-full md:w-auto bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700">Logout</button>
                            <button className="w-full md:w-auto bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300">Change Password</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;